import type { ReactNode } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { useStaffWorkspace, type StaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { Icon } from "../../../components/Icon";
import { TarifHinweis, useInsightsBereit, useTarifStand } from "../../../components/Tarif";
import { TARIF_GRUPPEN, modulGesperrt } from "../../../model/tarif";
import { today as heute } from "../../../lib/format";
import { t, ta } from "../../../lib/sprache";
import { loadAbo } from "./data";
import { KonditionenTeil } from "./Konditionen";
import { KlappOffen } from "./Klappabschnitt";
import { ENTITLEMENT_LABELS, MODULE_HINTS } from "./model";
import { NurAdmin, SettingsFrame } from "./parts";
import { CreatorSicht } from "./CreatorSicht";
import { HerkunftNachlauf } from "./HerkunftNachlauf";
import { GutschriftERechnung, NummernkreisTabelle } from "./Nummernkreis";
import { ArtikelBereich } from "./Vorlagen";
import { MODUL_ORT, VertraegePanel } from "./Abo";
import { KoopBereich, type KoopTeil } from "../kooperationen/Einstellungen";
import { AngebotsEinstellungen } from "../anfragen/Einstellungen";
import { EINST, KARTEN, karteGesperrt, seiteSichtbar, type SeiteKey } from "./baum";

/**
 * IA-1 (21.10.2026): die Einzelseiten von „Alle Einstellungen“, die aus der früheren Seite „Konditionen“ und aus „Abo &
 * Tarif“ herausgelöst sind. Jede Seite hat EIN Thema und dieselben Speicherwege wie vorher; Abschnitte stehen offen
 * (KlappOffen). Nur der Admin öffnet sie; Seiten, die es im Creator-Konto nicht gibt, führen dort zur Startseite.
 */
function Schutz({ seite, children }: { seite: SeiteKey; children: (ws: StaffWorkspace) => ReactNode }) {
  const ws = useStaffWorkspace();
  const stand = useTarifStand(ws.isChef ? ws.org.org_id : null);
  // I-2: gesperrte Seiten (Manager, im Creator-Konto nicht vorhanden) führen zur Startseite „Alle Einstellungen“
  if (!ws.isChef || !seiteSichtbar(seite, { chef: true, creator: !!ws.isCreator })) return <NurAdmin />;
  // I-1: im Tarif nicht enthalten: Seite mit Hinweis „Im Tarif … enthalten“ und Upgrade statt des Formulars
  const k = KARTEN.find((x) => x.seite === seite && x.modul);
  if (k?.modul && karteGesperrt(k, stand.data?.limits)) {
    return (
      <SettingsFrame section="abo" seite={seite} orgId={ws.org.org_id} title={k.titel()}>
        <TarifHinweis modul={k.modul} was={k.titel()} />
      </SettingsFrame>
    );
  }
  return <>{children(ws)}</>;
}

// ── Aus den Konditionen: Formularseiten (ein Formular, feste Speichern-Leiste) ──

export function Provision() {
  return (
    <Schutz seite="provision">{(ws) => (
      <KonditionenTeil orgId={ws.org.org_id} teile={["provision"]} seite="provision" title={t("Provision", "Commission")}
        text={t("Standardprovision und Agenturgebühr für neue Kooperationen.", "Default commission and agency fee for new deals.")}
        darunter={<p className="notice tk-verweise"><Link to={`${EINST}/staffel`}>{t("Gestaffelte Provision je Creator", "Tiered commission per creator")}</Link>{" · "}<Link to={`${EINST}/herkunft-nachprovision`}>{t("Herkunft & Nachprovision", "Origin & post-term commission")}</Link></p>} />
    )}</Schutz>
  );
}

export function Abrechnungsweg() {
  return (
    <Schutz seite="abrechnungsweg">{(ws) => (
      <KonditionenTeil orgId={ws.org.org_id} teile={["abrechnungsweg"]} seite="abrechnungsweg" title={t("Abrechnungsweg", "Invoicing route")}
        text={t("Wer wem eine Rechnung stellt und wann die Agentur an Creator auszahlt.", "Who invoices whom and when the agency pays creators.")} />
    )}</Schutz>
  );
}

export function Zahlungsziele() {
  return (
    <Schutz seite="zahlungsziele">{(ws) => (
      <KonditionenTeil orgId={ws.org.org_id} creator={!!ws.isCreator} teile={["zahlungsziele"]} seite="zahlungsziele" title={t("Zahlungsziele", "Payment terms")}
        text={ta("Zahlungsziele für Rechnungen und die Fristen von Signierlinks und Einladungen.", "Payment terms for invoices and the deadlines of signing links and invitations.", "Zahlungsziele für Rechnungen und die Frist von Signierlinks.", "Payment terms for invoices and the deadline of signing links.")} />
    )}</Schutz>
  );
}

export function Pruefregeln() {
  return (
    <Schutz seite="pruefregeln">{(ws) => (
      <KonditionenTeil orgId={ws.org.org_id} creator={!!ws.isCreator} teile={["pruefregeln"]} seite="pruefregeln" title={t("Prüfregeln", "Check rules")}
        text={t("Hinweise vor dem Festschreiben einer Rechnung.", "Notices before an invoice is finalised.")} />
    )}</Schutz>
  );
}

export function Wiedervorlagen() {
  return (
    <Schutz seite="wiedervorlagen">{(ws) => (
      <KonditionenTeil orgId={ws.org.org_id} creator={!!ws.isCreator} teile={["wiedervorlagen"]} seite="wiedervorlagen" title={t("Wiedervorlagen", "Follow-ups")}
        text={t("Ab wann Liegengebliebenes als dringend gilt und welche Feiertage zählen.", "When stalled items count as urgent and which public holidays count.")} />
    )}</Schutz>
  );
}

export function Verguetung() {
  return (
    <Schutz seite="verguetung">{(ws) => (
      <KonditionenTeil orgId={ws.org.org_id} creator={!!ws.isCreator} teile={["verguetung"]} seite="verguetung" title={t("Vergütungsmodelle", "Remuneration models")}
        text={t("Zugelassene Vergütungsmodelle und Vorgaben für Verträge und das Creator-Profil.", "Permitted remuneration models and requirements for contracts and the creator profile.")} />
    )}</Schutz>
  );
}

// ── Tabellen und eigene Bereiche ──

export function Nummernkreise() {
  return (
    <Schutz seite="nummernkreise">{(ws) => (
      <SettingsFrame section="konditionen" seite="nummernkreise" orgId={ws.org.org_id} title={t("Nummernkreise", "Number ranges")}
        text={t("Präfix, Muster und nächste Nummer je Belegart.", "Prefix, pattern and next number per document type.")}>
        <NummernkreisTabelle orgId={ws.org.org_id} creator={!!ws.isCreator} />
      </SettingsFrame>
    )}</Schutz>
  );
}

function KoopSeite({ seite, teil, title, text, darunter }: { seite: SeiteKey; teil: KoopTeil; title: string; text: string; darunter?: ReactNode }) {
  return (
    <Schutz seite={seite}>{(ws) => (
      <SettingsFrame section="konditionen" seite={seite} orgId={ws.org.org_id} title={title} text={text}>
        <KlappOffen><KoopBereich orgId={ws.org.org_id} teil={teil} /></KlappOffen>
        {darunter}
      </SettingsFrame>
    )}</Schutz>
  );
}

export function Positionsarten() {
  const ws = useStaffWorkspace();
  return (
    <KoopSeite seite="positionsarten" teil="positionsarten" title={t("Positionsarten & Artikel", "Item types & articles")}
      text={t("Arten von Zusatzpositionen und der Artikelkatalog für Rechnungen.", "Types of extra items and the item catalogue for invoices.")}
      darunter={<ArtikelBereich orgId={ws.org.org_id} currency={ws.org.currency} />} />
  );
}
export function Plattformen() {
  return <KoopSeite seite="plattformen" teil="plattformen" title={t("Plattformen", "Platforms")} text={t("Plattformen, die im Kooperationsformular zur Wahl stehen.", "Platforms offered in the deal form.")} />;
}
export function KooperationenEinstellung() {
  return <KoopSeite seite="kooperationen" teil="grundwerte" title={t("Kooperationen", "Deals")} text={t("Korrekturschleifen, Umsatzbeginn und Vorgaben für neue Kooperationen.", "Revision rounds, revenue start and defaults for new deals.")} />;
}
export function Staffel() {
  return <KoopSeite seite="staffel" teil="staffel" title={t("Gestaffelte Provision", "Tiered commission")} text={t("Provision je Creator nach Umsatzstufen.", "Commission per creator by revenue tiers.")} />;
}
export function MarkenFreigabe() {
  return <KoopSeite seite="marken-freigabe" teil="marke" title={t("Marken-Freigabe", "Brand approval")} text={t("Freigabe von Entwürfen durch die Marke und der Bericht danach.", "Approval of drafts by the brand and the report afterwards.")} />;
}

function EinfacheSeite({ seite, title, text, children }: { seite: SeiteKey; title: string; text: string; children: (ws: StaffWorkspace) => ReactNode }) {
  return (
    <Schutz seite={seite}>{(ws) => (
      <SettingsFrame section="konditionen" seite={seite} orgId={ws.org.org_id} title={title} text={text}>
        <KlappOffen>{children(ws)}</KlappOffen>
      </SettingsFrame>
    )}</Schutz>
  );
}

export function AnfragenEinstellung() {
  return (
    <EinfacheSeite seite="anfragen" title={t("Anfragen & Angebote", "Inquiries & offers")} text={t("Gültigkeit, Nachfassen, Preise und Standardtexte der Angebote.", "Validity, follow-up, prices and default texts of offers.")}>
      {(ws) => <AngebotsEinstellungen orgId={ws.org.org_id} today={heute()} />}
    </EinfacheSeite>
  );
}
export function CreatorSichtSeite() {
  return (
    <EinfacheSeite seite="creator-sicht" title={t("Creator-Sicht", "Creator view")} text={t("Was Creator im Creator-Portal sehen.", "What creators see in the creator portal.")}>
      {(ws) => <CreatorSicht orgId={ws.org.org_id} />}
    </EinfacheSeite>
  );
}
export function HerkunftSeite() {
  return (
    <EinfacheSeite seite="herkunft-nachprovision" title={t("Herkunft & Nachprovision", "Origin & post-term commission")} text={t("Woher eine Kooperation kommt und was nach Vertragsende gilt.", "Where a deal comes from and what applies after the contract ends.")}>
      {(ws) => <HerkunftNachlauf orgId={ws.org.org_id} />}
    </EinfacheSeite>
  );
}
export function Gutschriften() {
  return (
    <EinfacheSeite seite="gutschriften" title={t("Gutschriften", "Self-billing")} text={t("Gutschriften an Creator als E-Rechnung; das Nummernmuster steht bei den Nummernkreisen.", "Self-billing invoices to creators as e-invoices; the number pattern is under Number ranges.")}>
      {(ws) => (
        <section className="panel stack">
          <GutschriftERechnung orgId={ws.org.org_id} />
          <p className="tk-verweise"><Link to={`${EINST}/nummernkreise`}>{t("Muster der Gutschriftnummern: Nummernkreise", "Self-billing number pattern: Number ranges")}</Link></p>
        </section>
      )}
    </EinfacheSeite>
  );
}

// ── Aus Abo & Tarif ──

/** Verträge mit CreatorOffice, Fassungen, Zustimmungen und Nachweise (früher aufklappbar unter Abo & Tarif). */
export function Recht() {
  return (
    <Schutz seite="recht">{(ws) => (
      <SettingsFrame section="abo" seite="recht" orgId={ws.org.org_id} title={t("Verträge & Rechtstexte", "Contracts & legal texts")}
        text={t("Verträge mit CreatorOffice, aktuelle und frühere Fassungen und die Zustimmungen mit Nachweis.", "Contracts with CreatorOffice, current and earlier versions and the consents with proof.")}>
        <VertraegePanel orgId={ws.org.org_id} orgName={ws.org.name} timeZone={ws.org.timezone} offen />
      </SettingsFrame>
    )}</Schutz>
  );
}

/**
 * „Was ist in meinem Tarif an“: nur lesend aus den Tarifrechten (org_entitlements), je Modul ein Häkchen und wo es eingestellt
 * wird; ändern geht unter Abo & Tarif.
 */
export function ModuleUebersicht() {
  return (
    <Schutz seite="module">{(ws) => (
      <SettingsFrame section="abo" seite="module" orgId={ws.org.org_id} title={t("Was ist in meinem Tarif an", "What my plan includes")}
        text={t("Welche Module dein Tarif freischaltet. Ändern und vergleichen unter Abo & Tarif.", "Which modules your plan enables. Change and compare under Subscription & plan.")}
        actions={<Link className="btn" to={`${EINST}/abo`}>{t("Abo & Tarif", "Subscription & plan")}</Link>}>
        <ModulListe orgId={ws.org.org_id} timeZone={ws.org.timezone} />
      </SettingsFrame>
    )}</Schutz>
  );
}

function ModulListe({ orgId, timeZone }: { orgId: string; timeZone: string }) {
  const stand = useTarifStand(orgId);
  const insightsBereit = useInsightsBereit();
  // Name des Tarifs wie auf Abo & Tarif (gleicher Abfrageschlüssel, im Zwischenspeicher meist schon da)
  const abo = useQuery({ queryKey: ["subscriptions", orgId, "abo"], queryFn: () => loadAbo(api, orgId, timeZone) });
  if (stand.error) return <ErrorState error={stand.error} retry={() => void stand.refetch()} />;
  if (stand.isPending) return <Loading />;
  const limits = stand.data.limits;
  const plan = abo.data?.plans.find((p) => p.key === stand.data.plan);
  return (
    <section className="panel stack tk-module-liste" aria-labelledby="module-liste-title">
      <h2 id="module-liste-title">{plan ? t(`Module im Tarif ${plan.name}`, `Modules in the ${plan.name} plan`) : t("Module im Tarif", "Modules in the plan")}</h2>
      {stand.data.fullTrial && <p className="notice">{t("Im Test ist alles freigeschaltet. Nach dem Test gilt, was dein gewählter Tarif enthält.", "Everything is enabled during the trial. After the trial, what your chosen plan includes applies.")}</p>}
      {TARIF_GRUPPEN.filter((g) => g.key !== "grundlagen").map((g) => (
        <div key={g.key} className="stack tk-module-gruppe">
          <h3>{g.label}</h3>
          <ul className="tk-module-zeilen">
            {g.keys.map((k) => {
              const vorbereitung = k === "module_insights" && !insightsBereit;
              const an = !vorbereitung && !modulGesperrt(limits, k);
              const ort = MODUL_ORT[k];
              return (
                <li key={k} className={an ? "an" : "aus"}>
                  <span className={an ? "pill success" : "pill"} aria-hidden="true">{an ? "✓" : <Icon name="lock" />}</span>
                  <span className="tk-module-text">
                    <strong>{ENTITLEMENT_LABELS[k]}</strong>
                    <small>{MODULE_HINTS[k]}</small>
                  </span>
                  <span className="tk-module-stand">
                    {vorbereitung ? t("In Vorbereitung", "In preparation") : an ? t("Enthalten", "Included") : t("Nicht enthalten", "Not included")}
                    {an && ort && <> · <Link to={ort.to}>{ort.label()}</Link></>}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
}
